import { ListItem, Text, UnorderedList, useToast } from "@chakra-ui/react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useBeginEvaluationMutation,
  useCompleteSourcingEventV2Mutation,
  useConfirmAwardMutation,
  useReopenCompletedSourcingEventV2Mutation,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { bidDocuments } from "../award/bidDocuments";
import { CreateContractFromBidModal } from "../award/CreateContractFromBidModal";
import { requiresStandstill } from "../award/requiresStandstill";
import { scrollToEventTabs } from "../eventTabsAnchor";
import { useGoToEventTab } from "../useGoToEventTab";
import {
  deriveNowPanel,
  type NowAction,
  type NowActionTarget,
  type NowCommandTarget,
} from "./deriveNowPanel";
import { NowPanel, type NowPanelAction } from "./NowPanel";
import { useNowPanelData } from "./useNowPanelData";

export const EventNowPanel = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger, triggerSoft } = useApiError();
  const confirmationModal = useConfirmationModal();
  const goToTab = useGoToEventTab();
  const [beginEvaluation, { isLoading: isBeginning }] = useBeginEvaluationMutation();
  const [completeEvaluation, { isLoading: isCompleting }] = useCompleteSourcingEventV2Mutation();
  const [reopenEvaluation, { isLoading: isReopening }] = useReopenCompletedSourcingEventV2Mutation();
  const [confirmAward, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const { award, isAwarding, winnerIds } = useAwardSelection();
  const [contractBidId, setContractBidId] = useState<string | null>(null);
  const data = useNowPanelData(event);
  const state = deriveNowPanel(data);
  const contractBid = data.bids.find((bid) => bid.id === contractBidId);

  const pending: Record<NowCommandTarget["command"], boolean> = {
    beginEvaluation: isBeginning,
    completeEvaluation: isCompleting,
    reopenEvaluation: isReopening,
    awardEvent: isAwarding,
    confirmAward: isConfirming,
    createContract: false,
  };

  const run = async (target: NowCommandTarget) => {
    try {
      switch (target.command) {
        case "createContract":
          setContractBidId(target.bidId);
          return;
        case "beginEvaluation":
          await beginEvaluation({ eventId: event.id }).unwrap();
          return;
        case "completeEvaluation":
          await completeEvaluation({ eventId: event.id }).unwrap();
          return;
        case "reopenEvaluation":
          await reopenEvaluation({ eventId: event.id }).unwrap();
          return;
        case "awardEvent":
          await award();
          return;
        case "confirmAward":
          await confirmAward({ eventId: event.id }).unwrap();
          toast({ title: t("Award confirmed"), status: "success", position: "top-right" });
      }
    } catch (e) {
      if (target.command === "beginEvaluation") triggerSoft(e);
      else trigger(e);
    }
  };

  const confirmationFor = (
    command: NowCommandTarget["command"],
  ): { title: string; description: ReactNode } | null => {
    switch (command) {
      case "completeEvaluation":
        return {
          title: t("Complete the evaluation?"),
          description: t("The scores and the price selection are frozen. You can reopen it later."),
        };
      case "reopenEvaluation":
        return {
          title: t("Reopen the evaluation?"),
          description: t("The procurement is moved back to evaluation."),
        };
      case "awardEvent": {
        const winners = data.bids.filter((bid) => winnerIds.includes(bid.id));
        const withStandstill = requiresStandstill(event);
        return {
          title: t("Award to the selected suppliers?"),
          description: (
            <>
              <Text>{t("Do you want to award the contract to:")}</Text>
              <UnorderedList mt="2" mb={withStandstill ? "3" : undefined}>
                {winners.map((bid) => (
                  <ListItem key={bid.id}>{bid.ownerName}</ListItem>
                ))}
              </UnorderedList>
              {withStandstill && <Text>{t("The award letters go out and the standstill period starts.")}</Text>}
            </>
          ),
        };
      }
      case "confirmAward":
        return {
          title: t("Confirm the award?"),
          description: t("The award is final and the procurement moves on to the contract."),
        };
      default:
        return null;
    }
  };

  const go = (target: NowActionTarget) => {
    if (target.kind === "tab") {
      goToTab(target.tab, target.subTab);
      scrollToEventTabs();
      return;
    }
    if (target.kind === "url") {
      navigate(target.url);
      return;
    }
    const confirmation = confirmationFor(target.command);
    if (confirmation == null) {
      void run(target);
      return;
    }
    confirmationModal.show({ ...confirmation, onConfirmed: () => run(target) });
  };

  const toPanelAction = (candidate: NowAction): NowPanelAction => ({
    label: t(candidate.labelKey),
    onClick: () => go(candidate.target),
    isLoading: candidate.target.kind === "command" && pending[candidate.target.command],
  });

  if (state == null) return null;
  const { action, secondary } = state;

  return (
    <>
      {confirmationModal.modal}
      <NowPanel
        status={t(state.status.key, {
          ...state.status.params,
          ...(state.status.dateIso ? { date: displayDate(state.status.dateIso) } : {}),
        })}
        detail={state.detail.map((segment) => t(segment.key, segment.params ?? {})).join(". ") || undefined}
        progress={state.progress}
        action={action && toPanelAction(action)}
        secondaryAction={secondary && toPanelAction(secondary)}
      />
      {contractBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractBid.id}
          supplierName={contractBid.ownerName}
          defaultTitle={event.eventData.title}
          documents={bidDocuments(contractBid)}
          isOpen={contractBidId !== null}
          onClose={() => setContractBidId(null)}
        />
      )}
    </>
  );
};
